import { useState, useCallback } from 'react';
import { search as apiSearch, toLegacyQuoteCard } from '@/lib/api';
import type { DirectAnswer, LegacyQuoteCard } from '@/types';

interface SearchState {
  query: string;
  results: LegacyQuoteCard[];
  sources: string[];
  answer: DirectAnswer | null;
  isSearching: boolean;
  hasSearched: boolean;
  error: string | null;
}

export function useSearch() {
  const [state, setState] = useState<SearchState>({
    query: '',
    results: [],
    sources: [],
    answer: null,
    isSearching: false,
    hasSearched: false,
    error: null,
  });

  const search = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) return;

    setState((previous) => ({
      ...previous,
      isSearching: true,
      query: searchQuery,
      error: null,
    }));

    try {
      const response = await apiSearch({
        q: searchQuery,
        limit: 20,
      });

      // Normalize API response to legacy format
      const normalizedResults = response.cards.map((card) => toLegacyQuoteCard(card));

      setState({
        query: searchQuery,
        results: normalizedResults,
        sources: response.sources,
        answer: response.answer,
        isSearching: false,
        hasSearched: true,
        error: null,
      });
    } catch (error) {
      console.error('[useSearch] Error:', error);
      setState((previous) => ({
        ...previous,
        isSearching: false,
        hasSearched: true,
        results: [],
        sources: [],
        answer: null,
        error: 'Search is temporarily unavailable. Please try again.',
      }));
    }
  }, []);

  const clearSearch = useCallback(() => {
    setState({
      query: '',
      results: [],
      sources: [],
      answer: null,
      isSearching: false,
      hasSearched: false,
      error: null,
    });
  }, []);

  const setQuery = useCallback((query: string) => {
    setState((prev) => ({ ...prev, query }));
  }, []);

  return {
    query: state.query,
    results: state.results,
    sources: state.sources,
    answer: state.answer,
    isSearching: state.isSearching,
    hasSearched: state.hasSearched,
    error: state.error,
    search,
    clearSearch,
    setQuery,
  };
}
